/**
 * Mirror of RPS `ErrorCodeV2` (one enum shared by every RPS v2 endpoint).
 * Keys only; the user-facing text lives in `src/utils/constants/apiErrors.js`.
 */
export enum RpsErrorCode {
  REQUESTER_RESOLVER_ERROR = "REQUESTER_RESOLVER_ERROR",
  UNAUTHORIZED_REQUESTER = "UNAUTHORIZED_REQUESTER",
  POINTS_BANK_NOT_FOUND = "POINTS_BANK_NOT_FOUND",
  CHARGE_DEFINITION_WITHOUT_CHARGE_SOURCE = "CHARGE_DEFINITION_WITHOUT_CHARGE_SOURCE",
  CHARGE_TEMPLATE_WITHOUT_CHARGE_SOURCE = "CHARGE_TEMPLATE_WITHOUT_CHARGE_SOURCE",
  CHARGE_TEMPLATE_NOT_FOUND = "CHARGE_TEMPLATE_NOT_FOUND",
  CHARGE_TEMPLATE_VERSION_MISMATCH = "CHARGE_TEMPLATE_VERSION_MISMATCH",
  CHARGE_TEMPLATE_ALREADY_REJECTED = "CHARGE_TEMPLATE_ALREADY_REJECTED",
  CHARGE_TEMPLATE_ALREADY_CANCELLED = "CHARGE_TEMPLATE_ALREADY_CANCELLED",
  CHARGE_TEMPLATE_ALREADY_APPROVED = "CHARGE_TEMPLATE_ALREADY_APPROVED",
  RESET_DEFINITION_NOT_FOUND = "RESET_DEFINITION_NOT_FOUND",
  REVIEWER_ACCESS_DENIED = "REVIEWER_ACCESS_DENIED",
  CANNOT_APPROVE_CHARGE_TEMPLATE = "CANNOT_APPROVE_CHARGE_TEMPLATE",
  CANNOT_REJECT_CHARGE_TEMPLATE = "CANNOT_REJECT_CHARGE_TEMPLATE",
  CANNOT_CANCEL_CHARGE_TEMPLATE = "CANNOT_CANCEL_CHARGE_TEMPLATE",
  CANNOT_UPDATE_CHARGE_TEMPLATE = "CANNOT_UPDATE_CHARGE_TEMPLATE",
  CANNOT_SET_TEMPLATE_STATUS = "CANNOT_SET_TEMPLATE_STATUS",
  CHARGE_DEFINITION_FOR_TEMPLATE_NOT_FOUND = "CHARGE_DEFINITION_FOR_TEMPLATE_NOT_FOUND",
  CANNOT_CREATE_CHARGE_TEMPLATE = "CANNOT_CREATE_CHARGE_TEMPLATE",
  INVALID_EXECUTION_TIME = "INVALID_EXECUTION_TIME",
  CANT_CHANGE_CHARGE_STATUS = "CANT_CHANGE_CHARGE_STATUS",
  INVALID_CSV_LINE = "INVALID_CSV_LINE",
  TOO_MANY_INVALID_CSV_LINES = "TOO_MANY_INVALID_CSV_LINES",
  CSV_FILE_NOT_EXIST = "CSV_FILE_NOT_EXIST",
  HTTP_MESSAGE_NOT_READABLE = "HTTP_MESSAGE_NOT_READABLE",
  ILLEGAL_ARGUMENT = "ILLEGAL_ARGUMENT",
  CHARGE_DEFINITION_ALREADY_EXISTS = "CHARGE_DEFINITION_ALREADY_EXISTS",
  CHARGE_DEFINITION_NOT_EXIST = "CHARGE_DEFINITION_NOT_EXIST",
  CHARGE_DEFINITION_CHANGE_NOT_FOUND = "CHARGE_DEFINITION_CHANGE_NOT_FOUND",
  CANNOT_CANCEL_CHARGE_DEFINITION = "CANNOT_CANCEL_CHARGE_DEFINITION",
  REQUEST_BODY_VALIDATION_ERROR = "REQUEST_BODY_VALIDATION_ERROR",
  REQUEST_PART_MISSING = "REQUEST_PART_MISSING",
  REQUIRED_QUERY_PARAMETER_MISSING = "REQUIRED_QUERY_PARAMETER_MISSING",
  METHOD_ARGUMENT_TYPE_MISMATCH = "METHOD_ARGUMENT_TYPE_MISMATCH",
  CONSTRAINT_VIOLATION = "CONSTRAINT_VIOLATION",
  REQUEST_BODY_FIELD_VALIDATION_ERROR = "REQUEST_BODY_FIELD_VALIDATION_ERROR",
  BATCH_UPDATE_ERROR = "BATCH_UPDATE_ERROR",
  CSV_FILE_ALREADY_EXISTS = "CSV_FILE_ALREADY_EXISTS",
  INVALID_FILE_STRUCTURE = "INVALID_FILE_STRUCTURE",
  UNKNOWN_PROPERTY_REFERENCE_ERROR = "UNKNOWN_PROPERTY_REFERENCE_ERROR",
  UNSUPPORTED_SORT_PROPERTY_ERROR = "UNSUPPORTED_SORT_PROPERTY_ERROR",
  FILE_READ_ERROR = "FILE_READ_ERROR",
  MULTIPART_READ_ERROR = "MULTIPART_READ_ERROR",
  FILE_UPLOAD_ERROR = "FILE_UPLOAD_ERROR",
  NOT_EXISTING_GROUPS = "NOT_EXISTING_GROUPS",
  LOCKED_RESOURCE = "LOCKED_RESOURCE",
  EXISTING_ACTIVE_CYCLIC_CHARGE_DEFINITION = "EXISTING_ACTIVE_CYCLIC_CHARGE_DEFINITION",
  ACCOUNT_OWNER_NOT_EXIST = "ACCOUNT_OWNER_NOT_EXIST",
  ACCOUNT_OWNER_WITHOUT_ACCOUNT = "ACCOUNT_OWNER_WITHOUT_ACCOUNT",
  ACCOUNT_NOT_FOUND_BY_OWNER_AND_BANK = "ACCOUNT_NOT_FOUND_BY_OWNER_AND_BANK",
  CHARGE_DEFINITION_NOT_IN_BANK = "CHARGE_DEFINITION_NOT_IN_BANK",
  CHARGE_DEFINITION_STATUS_INACTIVE = "CHARGE_DEFINITION_STATUS_INACTIVE",
  CHARGE_DEFINITION_VERSION_MISMATCH = "CHARGE_DEFINITION_VERSION_MISMATCH",
  FIRST_CHARGE_ALREADY_PASSED = "FIRST_CHARGE_ALREADY_PASSED",
  PERIODICITY_CHANGE_IMPOSSIBLE = "PERIODICITY_CHANGE_IMPOSSIBLE",
  LAST_CHARGE_ALREADY_PASSED = "LAST_CHARGE_ALREADY_PASSED",
  CANNOT_REPLACE_EXISTING_EXECUTION = "CANNOT_REPLACE_EXISTING_EXECUTION",
  CANNOT_CANCEL_ONE_TIME_CHARGE = "CANNOT_CANCEL_ONE_TIME_CHARGE",
  CANNOT_CHANGE_CHARGE_TYPE = "CANNOT_CHANGE_CHARGE_TYPE",
  CANNOT_CANCEL_RESET_DEFINITION = "CANNOT_CANCEL_RESET_DEFINITION",
  RESET_DEFINITION_ALREADY_EXISTS = "RESET_DEFINITION_ALREADY_EXISTS",
  RESET_DEFINITION_WITHOUT_RESET_SOURCE = "RESET_DEFINITION_WITHOUT_RESET_SOURCE",
  INCORRECT_RESET_DAYS = "INCORRECT_RESET_DAYS",
  RESET_GROUP_NOT_FOUND = "RESET_GROUP_NOT_FOUND",
  RESET_GROUP_INCOMPATIBLE_WITH_POINTS_BANK = "RESET_GROUP_INCOMPATIBLE_WITH_POINTS_BANK",
  FILE_REPORT_NOT_FOUND = "FILE_REPORT_NOT_FOUND",
  INVALID_PARAMETERS = "INVALID_PARAMETERS",
  REFERENCE_NOT_FOUND = "REFERENCE_NOT_FOUND",
}

/** A code the backend may send before this mirror is updated stays representable. */
export type RpsErrorCodeValue = RpsErrorCode | (string & {});

/** A CSV line error, in the shape the file errors popup lists. */
export interface RpsErrorRow {
  readonly line: string | number;
  readonly message: string;
}

/** One backend error after the apiErrors.js lookup. */
export interface RpsErrorItem {
  readonly code: RpsErrorCodeValue;
  /** Text from apiErrors.js; undefined when the code has no entry. */
  readonly message: string | undefined;
  /** Set for INVALID_CSV_LINE and INVALID_FILE_STRUCTURE that carry details. */
  readonly row: RpsErrorRow | undefined;
}

/**
 * Error with the backend message already translated. Keeps the backend codes
 * and HTTP status so a flow can recognise a specific failure (e.g. a version
 * conflict) without parsing the message.
 */
export class RechargePlanningSystemError extends Error {
  readonly codes: readonly RpsErrorCodeValue[];

  readonly status: number | undefined;

  /** Every backend error, in order; CSV line errors carry their row. */
  readonly items: readonly RpsErrorItem[];

  constructor(
    message: string,
    codes: readonly RpsErrorCodeValue[],
    status?: number,
    items: readonly RpsErrorItem[] = []
  ) {
    super(message);
    Object.setPrototypeOf(this, RechargePlanningSystemError.prototype);
    this.codes = codes;
    this.status = status;
    this.items = items;
  }
}
